import { toApiDateString } from '@/v2/utils/dateHelpers'
import { BOOLEAN_FIELDS } from './constants'

const ARRAY_FIELDS = ['dental_conditions', 'medical_issues']

// The API camelizes every response key (config/initializers/jbuilder.rb sets
// `Jbuilder.key_format camelize: :lower`), so responses come back as
// `legalName` / `signatureEnabled` / `dentalConditions` while this form — and
// the request payload — use the model's snake_case names.
//
// `address_1` / `address_2` are the exception in both directions: they camelize
// to `address1` / `address2` on the way out, but the request side must keep the
// underscore. `treated_params` underscores request keys, and `"address1"
// .underscore` stays `"address1"`, which the strong-params permit list drops
// silently. See docs/v2/public/rest/intake_forms_doc.md "Notes and gotchas".
const RESPONSE_KEY_OVERRIDES = { address1: 'address_1', address2: 'address_2' }

export const toFormKey = key =>
  RESPONSE_KEY_OVERRIDES[key] ||
  key.replace(/([A-Z])/g, char => `_${char.toLowerCase()}`)

export const toFormKeys = (object = {}) =>
  Object.entries(object || {}).reduce((acc, [key, value]) => {
    acc[toFormKey(key)] = value
    return acc
  }, {})

export const buildPayload = (formValues = {}) => {
  const intake_response = {}

  Object.entries(formValues).forEach(([key, value]) => {
    if (value === undefined) return
    if (BOOLEAN_FIELDS.includes(key)) {
      if (value === 'true') intake_response[key] = true
      else if (value === 'false') intake_response[key] = false
      // unset boolean → omit so the server sees nil
      return
    }
    if (ARRAY_FIELDS.includes(key)) {
      intake_response[key] = Array.isArray(value) ? value : []
      return
    }
    if (key === 'birthdate') {
      intake_response.birthdate = toApiDateString(value)
      return
    }
    intake_response[key] = value
  })

  ARRAY_FIELDS.forEach(field => {
    if (!Array.isArray(intake_response[field])) intake_response[field] = []
  })

  return { intake_response }
}

// A 422 comes back as `{ errors: { legalName: ["This field is required."] } }`.
// requestMiddleware collapses that to `{ error: { legalName: "message" } }`, so
// accept either shape and re-key it to the field names the sections render.
export const mapServerErrors = (errors = {}) =>
  Object.entries(errors || {}).reduce((acc, [key, value]) => {
    acc[toFormKey(key)] = Array.isArray(value) ? value[0] : value
    return acc
  }, {})

// The show endpoint ships the option catalogs (`genders`, `dentalConditions`,
// `medicalIssues`) as {value, label} pairs. Fall back to the bundled constants
// so the staff read-only view — which reads the private endpoint and gets no
// catalogs — still renders labels.
export const normalizeOptions = (options = {}, fallbacks = {}) => {
  const normalized = toFormKeys(options)

  return Object.entries(fallbacks).reduce((acc, [field, fallback]) => {
    const fromApi = normalized[field]
    acc[field] = Array.isArray(fromApi) && fromApi.length ? fromApi : fallback
    return acc
  }, {})
}
